import { createDefaultImageReader } from '@messenger/pintura'
import { getImageDimensions } from './getImageDimensions'

type Size = { width: number; height: number }

/**
 * Pintura loads an http(s) source with this query, so the browser fetches it
 * with CORS instead of reusing the plain copy an <img> already cached, which
 * would taint the canvas. A blob or data URL is local and is left alone.
 */
export const withCorsQuery = (url: string) =>
  /^https?:/i.test(url)
    ? `${url}${url.includes('?') ? '&' : '?'}CORSRequest=true`
    : url

/**
 * `file` drawn at `size`, as a PNG so its transparency is kept, or `file`
 * itself when it already has that size.
 */
const fileAtSize = async (file: File, size: Size): Promise<File> => {
  const bitmap = await createImageBitmap(file)
  try {
    if (bitmap.width === size.width && bitmap.height === size.height)
      return file
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('No 2d canvas context to size the cutout')
    context.drawImage(bitmap, 0, 0, size.width, size.height)
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (result) =>
          result
            ? resolve(result)
            : reject(new Error('Could not encode the sized cutout')),
        'image/png',
      ),
    )
    return new File([blob], 'cutout.png', { type: 'image/png' })
  } finally {
    bitmap.close()
  }
}

/**
 * A Pintura image reader that loads an AI cutout at the size of the photo it
 * replaces. An item's saved Pintura edits (its crop and its markup) are
 * measured in that photo's pixels, and the model can answer at another
 * resolution with the same proportions, so the cutout is drawn at the photo's
 * size before Pintura reads it. The saved edits then land where they did on
 * the photo, and an export from the cutout saves edits that still fit the
 * photo. A cutout already at the photo's size is read as it is.
 */
export const createCutoutImageReader = (photoUrl: string) =>
  createDefaultImageReader({
    preprocessImageFile: async (file) =>
      fileAtSize(file, await getImageDimensions(photoUrl)),
  })
